<template>
  <div :class="{ 'top-navigation': hideMenu || !appLoaded }">
    <!-- Bandeau d'environnement (hors build production) : dans le flux, il pousse la page
         vers le bas. Fermable, mais non mémorisé : il revient à chaque rechargement. -->
    <div v-if="envBanner" :class="['env-banner', 'env-banner--' + envBanner.variant]" role="status">
      <span>{{ envBanner.label }}</span>
      <button type="button" class="env-banner__close" aria-label="Fermer" title="Fermer"
        @click="envBanner = null">&times;</button>
    </div>
    <div class="maintenance_incoming_message" v-if="maintenanceIncomingMessage">
      <p><i class="fa fa-exclamation-circle fa-lg"></i> {{ maintenanceIncomingMessage }}</p>
    </div>
    <Nav v-show="!hideMenu && appLoaded" :class="{ show: showMobileMenu }" @hideMobileMenu="showMobileMenu = false"></Nav>
    <div id="page-wrapper" class="gray-bg">
      <b-row>
        <b-col>
          <nav v-if="!hideHeader && appLoaded" class="navbar navbar-static-top white-bg d-flex" role="navigation"
            style="margin-bottom: 0">
            <TopSearch class="navbar-header flex-grow-1 d-flex align-self-center" />
            <ul class="nav navbar-top-links navbar-right">
              <!-- INC-001 : entrepôt de rattachement de l'utilisateur connecté -->
              <li v-if="warehouse" class="align-self-center mr-2">
                <span class="topbar-warehouse">
                  <i class="far fa-warehouse"></i>
                  {{ warehouse.name }}
                </span>
              </li>
              <li>
                <b-dropdown class="m-md-2" variant="outline-secondary">
                  <template slot="button-content">
                    <i class="far fa-user-cog fa-lg"></i>
                    {{ fullname }}
                    <i class="far fa-angle-down"></i>
                  </template>
                  <b-dropdown-item>
                    <router-link :to="'/security/logout'">
                      <i class="fa fa-sign-out"></i> Se déconnecter
                    </router-link>
                  </b-dropdown-item>
                </b-dropdown>
              </li>
            </ul>
          </nav>
          <nav class="navbar navbar-static-top white-bg navbar-mobile" style="margin-bottom: 0" ref="navbar-mobile">
            <div class="nav-toggle" @click="showMobileMenu = true">
              <i class="far fa-bars fa-lg"></i>
            </div>
          </nav>
        </b-col>
      </b-row>
      <div :class="['wrapper', 'wrapper-content', 'animated', 'fadeInRight', $route.name]">
        <div
          v-if="!appLoaded && $route.name != 'Unauthorized' && $route.name != 'UnauthorizedWithMessage' && $route.name != 'ErrorNotFound'"
          class="middle-box text-center loginscreen animated fadeInDown">
          <div>
            <div>
              <img class="loading-logo" src="/static/logo-my-emateriel.svg" />
            </div>
            <h3>Bienvenue dans MY E-MATERIEL</h3>
            <p>L'application est en cours de chargement...</p>
          </div>
        </div>
        <router-view />
      </div>
      <div v-if="!hideFooter && appLoaded" class="footer">
        <div class="float-right">v {{ version }}</div>
        <div>
          Création
          <strong>Kromi</strong>
        </div>
      </div>
    </div>

    <Loader :visible="showLoader"></Loader>

  </div>
</template>

<script>
import Nav from "@/components/Global/Nav";
import Loader from "@/components/Global/Loader";
import TopSearch from "@/components/Global/TopSearch";

export default {
  name: "App",
  data() {
    return {
      version: process.env.VERSION,
      currentRoute: window.location.pathname,
      showLoader: false,
      hideHeader: false,
      hideFooter: false,
      hideMenu: false,
      showMobileMenu: false,
      envBanner: {
        development: { label: "Développement", variant: "dev" },
        staging: { label: "PRÉPROD", variant: "preprod" },
        test: { label: "PRÉPROD", variant: "preprod" }
      }[process.env.NODE_ENV] || null,
    };
  },
  created() {
    this.hideHeader = this.$route.meta.hideHeader || false;
    this.hideFooter = this.$route.meta.hideFooter || false;
    this.hideMenu = this.$route.meta.hideMenu || false;
  },
  computed: {
    ViewComponent() {
      return routes[this.currentRoute] || NotFound;
    },
    fullname() {
      return this.$store.getters["auth/fullname"];
    },
    // INC-001 : entrepôt de rattachement (objet {id, name, internal_code}) ou null = accès à tous
    warehouse() {
      return this.$store.getters["auth/warehouse"];
    },
    appLoaded() {
      return this.$store.getters["app/isLoaded"];
    },
    maintenanceIncomingMessage() {
      return this.$store.getters["app/maintenanceIncomingMessage"];
    }
  },
  components: {
    Nav,
    Loader,
    TopSearch
  },
  watch: {
    $route(newRoute, oldRoute) {
      this.hideHeader = newRoute.meta.hideHeader || false;
      this.hideFooter = newRoute.meta.hideFooter || false;
      this.hideMenu = newRoute.meta.hideMenu || false;
    }
  },
  render(h) {
    return h(this.ViewComponent);
  }
};
</script>

<style lang="css">
@import "~vue-wysiwyg/dist/vueWysiwyg.css";
@import "/static/fontawesome/css/all.min.css";
</style>
<style lang="scss">
@import "~@/assets/scss/inspinia/style.scss";
@import "~@/assets/scss/_global.scss";

// Écran de chargement : le SVG du logo mesure 685px de large en intrinsèque et
// débordait de la .middle-box (300px). On le contraint et on le centre.
.loading-logo {
  display: block;
  width: 100%;
  max-width: 260px;
  height: auto;
  margin: 0 auto 50px;
}

// INC-001 : entrepôt de rattachement affiché dans la topbar
.topbar-warehouse {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #6c757d;
  white-space: nowrap;
}

// Bandeau d'environnement (hors build production), en haut de page et dans le flux
.env-banner {
  position: relative;
  padding: 6px 40px;
  color: #fff;
  font-weight: bold;
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-align: center;

  &--dev {
    background: #2e7d32;
  }

  &--preprod {
    background: #f57c00;
  }
}

.env-banner__close {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  background: none;
  border: 0;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue